import React from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  useTheme,
} from '@mui/material';
import {
  AccountBalance as BalanceIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  AccountBalanceWallet as WalletIcon,
  CalendarToday as CalendarIcon,
  MonetizationOn as FeeIcon,
} from '@mui/icons-material';

const formatBalance = (value, currency = 'USDT', showCurrency = true) => {
  if (value === null || value === undefined) return '—';

  const number = parseFloat(value);
  if (isNaN(number)) return '—';

  return new Intl.NumberFormat(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
    style: 'decimal',
    useGrouping: true
  }).format(number) + (showCurrency ? ` ${currency}` : '');
};

const StatBox = ({ icon: Icon, label, value, color = 'primary.main', currency = 'USDT' }) => {
  const theme = useTheme();

  return (
    <Box
      sx={{
        p: 2.5,
        borderRadius: 2,
        backgroundColor: theme.palette.mode === 'dark' ? 'background.default' : 'grey.50',
        border: `1px solid ${theme.palette.divider}`,
        display: 'flex',
        flexDirection: 'column',
        gap: 1.5,
        transition: 'all 0.3s ease',
        '&:hover': {
          boxShadow: theme.shadows[4],
          transform: 'translateY(-2px)'
        }
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Icon sx={{ color, fontSize: '1.3rem' }} />
        <Typography variant="body2" color="text.secondary" fontWeight={500}>
          {label}
        </Typography>
      </Box>
      <Typography
        variant="h5"
        fontWeight={700}
        color={color}
        sx={{ ml: 0.5 }}
      >
        {formatBalance(value, currency, true)}
      </Typography>
    </Box>
  );
};

const TotalBalanceCard = ({ users }) => {
  const theme = useTheme();

  if (!users || users.length === 0) {
    return null;
  }

  // Tính tổng các số dư
  const totals = users.reduce((acc, user) => {
    const marginBalance = parseFloat(user.margin_balance || 0);
    const walletBalance = parseFloat(user.wallet_balance || 0);
    const totalPnl = parseFloat(user.total_pnl || 0);
    const dailyPnl = parseFloat(user.daily_pnl || 0);
    const monthlyPnl = parseFloat(user.monthly_pnl || 0);
    const totalPnlSinceOct = parseFloat(user.total_pnl_since_oct || 0);

    // Tính phí
    const dailyTotalFees = parseFloat(user.daily_total_fees || 0);
    const monthlyTotalFees = parseFloat(user.monthly_total_fees || 0);

    // Debug log để kiểm tra dữ liệu
    console.log(`[TotalBalanceCard] User ${user.username}:`, {
      daily_pnl_raw: user.daily_pnl,
      dailyPnl,
      daily_fees: dailyTotalFees,
      monthly_fees: monthlyTotalFees
    });

    acc.marginBalance += Math.abs(marginBalance);
    acc.walletBalance += walletBalance;
    acc.totalPnl += totalPnl;
    acc.dailyPnl += dailyPnl;
    acc.monthlyPnl += monthlyPnl;
    acc.totalPnlSinceOct += totalPnlSinceOct;
    acc.dailyTotalFees += dailyTotalFees;
    acc.monthlyTotalFees += monthlyTotalFees;
    acc.availableBalance += (walletBalance - Math.abs(totalPnl) - Math.abs(marginBalance));
    acc.totalAsset += (walletBalance - Math.abs(totalPnl));

    return acc;
  }, {
    marginBalance: 0,
    walletBalance: 0,
    totalPnl: 0,
    dailyPnl: 0,
    monthlyPnl: 0,
    totalPnlSinceOct: 0,
    dailyTotalFees: 0,
    monthlyTotalFees: 0,
    availableBalance: 0,
    totalAsset: 0
  });

  console.log('[TotalBalanceCard] Totals:', totals);

  const getStatusColor = (value) => {
    if (value === null || value === undefined) return 'text.secondary';
    const num = parseFloat(value);
    if (isNaN(num)) return 'text.secondary';
    return num >= 0 ? 'success.main' : 'error.main';
  };

  return (
    <Card
      elevation={4}
      sx={{
        borderRadius: 3,
        background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
        border: `2px solid ${theme.palette.primary.light}`,
        mb: 4,
        overflow: 'visible',
        position: 'relative',
        '&:hover': {
          boxShadow: theme.shadows[12]
        },
        transition: 'all 0.3s ease-in-out'
      }}
    >
      <CardContent sx={{ p: 3 }}>
        {/* Header */}
        <Box sx={{ mb: 3 }}>
          <Typography
            variant="h5"
            sx={{
              fontWeight: 700,
              color: 'white',
              mb: 0.5,
              display: 'flex',
              alignItems: 'center',
              gap: 1
            }}
          >
            <BalanceIcon sx={{ fontSize: '1.8rem' }} />
            Tổng Số Dư Tất Cả Tài Khoản
          </Typography>
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)' }}>
            {users.length} tài khoản đang hoạt động
          </Typography>
        </Box>

        {/* Stats Grid */}
        <Grid container spacing={2}>
          {/* Hàng 1: 3 cột */}
          <Grid item xs={12} sm={6} md={4}>
            <StatBox
              icon={WalletIcon}
              label="Tổng Tài Sản"
              value={totals.walletBalance}
              color="info.main"
            />
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <StatBox
              icon={BalanceIcon}
              label="Đang Sử Dụng (Margin)"
              value={totals.marginBalance}
              color="warning.main"
            />
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <StatBox
              icon={BalanceIcon}
              label="Số Dư Còn Lại"
              value={totals.availableBalance}
              color="primary.main"
            />
          </Grid>

          {/* Hàng 2: 3 cột PnL */}
          <Grid item xs={12} sm={6} md={4}>
            <StatBox
              icon={totals.monthlyPnl >= 0 ? TrendingUpIcon : TrendingDownIcon}
              label="Lợi Nhuận Tháng Này"
              value={totals.monthlyPnl}
              color={getStatusColor(totals.monthlyPnl)}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <StatBox
              icon={CalendarIcon}
              label="Tổng Lãi Từ Tháng 10 Đến Nay"
              value={totals.totalPnlSinceOct}
              color={getStatusColor(totals.totalPnlSinceOct)}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <StatBox
              icon={totals.totalPnl >= 0 ? TrendingUpIcon : TrendingDownIcon}
              label="Tổng Lãi/Lỗ Hiện Tại"
              value={totals.totalPnl}
              color={getStatusColor(totals.totalPnl)}
            />
          </Grid>

          {/* Hàng 3: 2 cột phí */}
          <Grid item xs={12} sm={6} md={6}>
            <StatBox
              icon={FeeIcon}
              label="Phí Tổng Ngày"
              value={totals.dailyTotalFees}
              color="warning.main"
            />
          </Grid>

          <Grid item xs={12} sm={6} md={6}>
            <StatBox
              icon={FeeIcon}
              label="Phí Tổng Tháng"
              value={totals.monthlyTotalFees}
              color="warning.main"
            />
          </Grid>

          {/* Hàng 4: 2 cột nổi bật - Lợi nhuận hôm nay và Tổng tài sản còn lại */}
          <Grid item xs={12} md={6}>
            <Box
              sx={{
                p: 3,
                borderRadius: 2,
                background: `linear-gradient(135deg, ${totals.dailyPnl >= 0 ? '#2196f3' : '#f44336'} 0%, ${totals.dailyPnl >= 0 ? '#42a5f5' : '#ef5350'} 100%)`,
                border: `2px solid ${totals.dailyPnl >= 0 ? '#1976d2' : '#c62828'}`,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                transition: 'all 0.3s ease',
                height: '100%',
                minHeight: '100px',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: 6
                }
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                {totals.dailyPnl >= 0 ?
                  <TrendingUpIcon sx={{ color: 'white', fontSize: '2rem' }} /> :
                  <TrendingDownIcon sx={{ color: 'white', fontSize: '2rem' }} />
                }
                <Typography variant="h6" fontWeight={600} color="white">
                  Lợi Nhuận Hôm Nay
                </Typography>
              </Box>
              <Typography
                variant="h4"
                fontWeight={700}
                color="white"
              >
                {formatBalance(totals.dailyPnl, 'USDT', true)}
              </Typography>
            </Box>
          </Grid>

          <Grid item xs={12} md={6}>
            <Box
              sx={{
                p: 3,
                borderRadius: 2,
                background: `linear-gradient(135deg, ${totals.totalAsset >= 0 ? '#4caf50' : '#f44336'} 0%, ${totals.totalAsset >= 0 ? '#66bb6a' : '#ef5350'} 100%)`,
                border: `2px solid ${totals.totalAsset >= 0 ? '#388e3c' : '#c62828'}`,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                transition: 'all 0.3s ease',
                height: '100%',
                minHeight: '100px',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: 6
                }
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <WalletIcon sx={{ color: 'white', fontSize: '2rem' }} />
                <Typography variant="h6" fontWeight={600} color="white">
                  Tổng Tài Sản Còn Lại
                </Typography>
              </Box>
              <Typography
                variant="h4"
                fontWeight={700}
                color="white"
              >
                {formatBalance(totals.totalAsset, 'USDT', true)}
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};

export default TotalBalanceCard;
